import { AppStorage, ServiceClient } from 'src/utils'
import { ServiceError } from 'src/utils/ServiceError'
import {
    Alcance,
    Bloco,
    Campanha,
    CampanhaDetalhe,
    Diagnostico,
    Parceiro,
    PreviaDaExclusao,
    ResultadoDaExclusao,
    Situacao,
    TemplateDaCampanha
} from './Campanha.scopes'

const serviceClient = ServiceClient.singleton()
const appStorage = AppStorage.singleton()

const BASE_URL = '/api/campanha'

/** Formatos e tamanho aceitos pelo WhatsApp em mensagem de imagem. */
export const MIMES_ACEITOS = ['image/jpeg', 'image/png']
export const TAMANHO_MAXIMO_BYTES = 5 * 1024 * 1024

/** Limites de texto da Cloud API (o servidor valida os mesmos ao salvar). */
export const MAX_CARACTERES_TEXTO = 4096
export const MAX_CARACTERES_LEGENDA = 1024

type CampanhaApi = {
    newsId: string
    ativa: boolean
    disparadaEm?: string
    concluida: boolean
    concluidaEm?: string
    conteudoAtualizadoEm?: string
    criadaEm: string
    enviados: number
    enfileirados: number
    totalBlocos: number
}

type CampanhaDetalheApi = CampanhaApi & {
    blocos: BlocoApi[]
    testada: boolean
    testadaEm?: string
    template?: TemplateDaCampanha
}

const SEM_TEMPLATE: TemplateDaCampanha = {
    situacao: 'sem_template',
    problemas: [],
    podeCriar: false,
    foraDaJanela: false,
    enviando: false
}

type BlocoApi =
    | { tipo: 'texto'; texto: string; previewLink?: boolean }
    | { tipo: 'imagem'; assetId: number; legenda?: string; arquivo?: string; mimeType?: string; byteSize?: number }

const toDate = (value?: string) => (value ? new Date(value) : undefined)

function mapCampanha(item: CampanhaApi): Campanha {
    return {
        newsId: item.newsId,
        ativa: item.ativa,
        disparadaEm: toDate(item.disparadaEm),
        concluida: item.concluida,
        concluidaEm: toDate(item.concluidaEm),
        conteudoAtualizadoEm: toDate(item.conteudoAtualizadoEm),
        criadaEm: new Date(item.criadaEm),
        enviados: item.enviados,
        enfileirados: item.enfileirados,
        totalBlocos: item.totalBlocos
    }
}

function mapDetalhe(item: CampanhaDetalheApi): CampanhaDetalhe {
    return {
        ...mapCampanha(item),
        testada: item.testada,
        testadaEm: toDate(item.testadaEm),
        template: item.template ?? SEM_TEMPLATE,
        blocos: item.blocos.map<Bloco>((bloco) =>
            bloco.tipo === 'imagem'
                ? {
                      tipo: 'imagem',
                      assetId: bloco.assetId,
                      legenda: bloco.legenda,
                      arquivo: bloco.arquivo,
                      mimeType: bloco.mimeType,
                      byteSize: bloco.byteSize
                  }
                : { tipo: 'texto', texto: bloco.texto, previewLink: bloco.previewLink }
        )
    }
}

/** O que vai para a API: os campos só de exibição (arquivo, preview) ficam de fora. */
function toBlocoApi(bloco: Bloco): BlocoApi {
    return bloco.tipo === 'imagem'
        ? { tipo: 'imagem', assetId: bloco.assetId, legenda: bloco.legenda }
        : { tipo: 'texto', texto: bloco.texto, previewLink: bloco.previewLink }
}

export class CampanhaService {
    static #INSTANCE = new CampanhaService()
    static readonly singleton = () => CampanhaService.#INSTANCE

    async listar(): Promise<Campanha[]> {
        const response = await serviceClient.authGet<CampanhaApi[]>(BASE_URL)
        return response.map(mapCampanha)
    }

    async obter(newsId: string): Promise<CampanhaDetalhe> {
        return mapDetalhe(await serviceClient.authGet<CampanhaDetalheApi>(`${BASE_URL}/${encodeURIComponent(newsId)}`))
    }

    async salvar(newsId: string, blocos: Bloco[], ativa: boolean): Promise<CampanhaDetalhe> {
        const response = await serviceClient.authPost<CampanhaDetalheApi>(BASE_URL, {
            newsId,
            blocos: blocos.map(toBlocoApi),
            ativa
        })
        return mapDetalhe(response)
    }

    /**
     * Sobe a imagem com o arquivo cru no corpo (o servidor recebe nome e tipo pela query).
     * Não usa o ServiceClient porque este envia JSON.
     */
    async enviarImagem(arquivo: File, newsId?: string): Promise<{ assetId: number; byteSize: number }> {
        const accessToken = appStorage.accessToken
        if (!accessToken) {
            throw new ServiceError(401, 'Unauthorized')
        }

        const query = new URLSearchParams({ filename: arquivo.name, mime: arquivo.type })
        if (newsId) {
            query.set('newsId', newsId)
        }

        const response = await fetch(`${BASE_URL}/imagem?${query}`, {
            method: 'POST',
            headers: { Authorization: `BEARER ${accessToken}`, 'Content-Type': arquivo.type },
            body: arquivo
        })

        if (!response.ok) {
            throw new ServiceError(response.status, (await response.text()) || response.statusText)
        }

        return response.json()
    }

    /** A imagem vem de rota autenticada, então não dá para usar <img src> direto. */
    async baixarImagem(assetId: number): Promise<string> {
        const accessToken = appStorage.accessToken
        if (!accessToken) {
            throw new ServiceError(401, 'Unauthorized')
        }

        const response = await fetch(`${BASE_URL}/imagem/${assetId}`, {
            method: 'GET',
            headers: { Authorization: `BEARER ${accessToken}` }
        })

        if (!response.ok) {
            throw new ServiceError(response.status, response.statusText)
        }

        return URL.createObjectURL(await response.blob())
    }

    /** O relatório vem de rota autenticada, então não dá para usar <a href>: baixa por fetch. */
    async baixarRelatorio(newsId: string): Promise<{ nome: string; blob: Blob }> {
        const accessToken = appStorage.accessToken
        if (!accessToken) {
            throw new ServiceError(401, 'Unauthorized')
        }

        const response = await fetch(`${BASE_URL}/${encodeURIComponent(newsId)}/relatorio.csv`, {
            method: 'GET',
            headers: { Authorization: `BEARER ${accessToken}` }
        })

        if (!response.ok) {
            throw new ServiceError(response.status, response.statusText)
        }

        return { nome: `campanha-${newsId}.csv`, blob: await response.blob() }
    }

    async alcance(newsId: string): Promise<Alcance> {
        return serviceClient.authGet<Alcance>(`${BASE_URL}/${encodeURIComponent(newsId)}/alcance`)
    }

    async situacao(newsId: string): Promise<Situacao> {
        const response = await serviceClient.authGet<{
            newsId: string
            porStatus: Record<string, number>
            fila: Record<string, number>
            erros400: number
            entregas: {
                ownerId: string
                nome?: string
                status: string
                enfileiradaEm?: string
                enviadaEm?: string
                ultimaInteracaoEm?: string
            }[]
        }>(`${BASE_URL}/${encodeURIComponent(newsId)}/situacao`)

        return {
            newsId: response.newsId,
            porStatus: response.porStatus,
            fila: response.fila,
            erros400: response.erros400,
            entregas: response.entregas.map((item) => ({
                ownerId: item.ownerId,
                nome: item.nome,
                status: item.status,
                enfileiradaEm: toDate(item.enfileiradaEm),
                enviadaEm: toDate(item.enviadaEm),
                ultimaInteracaoEm: toDate(item.ultimaInteracaoEm)
            }))
        }
    }

    async listarParceiros(busca?: string): Promise<Parceiro[]> {
        const params = busca ? `?q=${encodeURIComponent(busca)}` : ''
        return serviceClient.authGet<Parceiro[]>(`${BASE_URL}/parceiros${params}`)
    }

    async diagnosticar(ownerId: string, newsId?: string): Promise<Diagnostico> {
        const params = new URLSearchParams({ owner: ownerId })
        if (newsId) {
            params.set('id', newsId)
        }

        const response = await serviceClient.authGet<
            Omit<Diagnostico, 'ultimaInteracaoEm'> & { ultimaInteracaoEm?: string }
        >(`${BASE_URL}/diagnostico?${params}`)

        return { ...response, ultimaInteracaoEm: toDate(response.ultimaInteracaoEm) }
    }

    async enviarTeste(newsId: string, ownerId: string): Promise<{ enfileirados: number }> {
        return serviceClient.authPost(`${BASE_URL}/${encodeURIComponent(newsId)}/teste`, { ownerId: Number(ownerId) })
    }

    async disparar(newsId: string): Promise<{ enfileirados: number }> {
        return serviceClient.authPost(`${BASE_URL}/${encodeURIComponent(newsId)}/disparo`)
    }

    /** Público do template (só admin): null = todos; 30, 60, 90 ou 180 dias. */
    async definirPublico(newsId: string, dias: number | null): Promise<CampanhaDetalhe> {
        return mapDetalhe(await serviceClient.authPost(`${BASE_URL}/${encodeURIComponent(newsId)}/publico`, { dias }))
    }

    async previaDaExclusao(newsId: string): Promise<PreviaDaExclusao> {
        return serviceClient.authGet<PreviaDaExclusao>(`${BASE_URL}/${encodeURIComponent(newsId)}/exclusao`)
    }

    /** Exclui a campanha; com `templates`, também os templates dela na Meta (só admin). */
    async excluir(newsId: string, templates: boolean): Promise<ResultadoDaExclusao> {
        return serviceClient.authDelete<ResultadoDaExclusao>(
            `${BASE_URL}/${encodeURIComponent(newsId)}?templates=${templates}`
        )
    }

    /** Cria o template da campanha e manda para a aprovação da Meta (só admin). */
    async criarTemplate(newsId: string): Promise<CampanhaDetalhe> {
        return mapDetalhe(await serviceClient.authPost(`${BASE_URL}/${encodeURIComponent(newsId)}/template`))
    }

    /** Liga/desliga o envio do template a quem está fora da janela (só admin). */
    async templateForaDaJanela(newsId: string, ligado: boolean): Promise<CampanhaDetalhe> {
        return mapDetalhe(
            await serviceClient.authPost(`${BASE_URL}/${encodeURIComponent(newsId)}/template/fora-da-janela`, {
                ligado
            })
        )
    }

    async pausar(newsId: string): Promise<CampanhaDetalhe> {
        return mapDetalhe(await serviceClient.authPost(`${BASE_URL}/${encodeURIComponent(newsId)}/pausar`))
    }

    async retomar(newsId: string): Promise<CampanhaDetalhe> {
        return mapDetalhe(await serviceClient.authPost(`${BASE_URL}/${encodeURIComponent(newsId)}/retomar`))
    }
}

export default CampanhaService.singleton()
